/**
 * WebhookDeliveriesTable - dead-letter / history view for outbound webhook
 * deliveries.
 *
 * Columns: Created, Endpoint name (resolved from `endpointsById`),
 * Event type, Status, HTTP status, Attempt, Error (truncated), Replay.
 *
 * "Replay" is only enabled for `failed_dead` rows since pending and
 * failed_retry deliveries are already going to be re-attempted by the
 * Celery beat. Success rows never replay.
 */
import { Badge, Button } from "@/components/ui";
import type {
  WebhookDeliveryResponse,
  WebhookDeliveryStatus,
  WebhookEndpointResponse,
} from "@/types/api";

function statusBadgeVariant(
  status: WebhookDeliveryStatus,
): "default" | "info" | "success" | "warning" | "error" {
  switch (status) {
    case "pending":
      return "info";
    case "success":
      return "success";
    case "failed_retry":
      return "warning";
    case "failed_dead":
      return "error";
  }
}

function statusLabel(status: WebhookDeliveryStatus): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "success":
      return "Success";
    case "failed_retry":
      return "Retrying";
    case "failed_dead":
      return "Dead";
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleString();
}

function truncate(text: string | null, max = 80): string {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

export interface WebhookDeliveriesTableProps {
  deliveries: WebhookDeliveryResponse[];
  /**
   * Lookup of endpoint id -> endpoint, used to render the endpoint
   * name column. Pass `{}` when the endpoint list is still loading;
   * the column falls back to the raw id.
   */
  endpointsById: Record<string, WebhookEndpointResponse>;
  onReplay: (deliveryId: number) => void;
  isReplayingId?: number | null;
}

export function WebhookDeliveriesTable({
  deliveries,
  endpointsById,
  onReplay,
  isReplayingId = null,
}: WebhookDeliveriesTableProps): JSX.Element {
  if (deliveries.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-8 text-center">
        <i
          className="fa-duotone fa-thin fa-truck text-3xl text-neutral-400"
          aria-hidden="true"
        />
        <p className="mt-3 text-sm font-medium text-neutral-900">
          No deliveries to show
        </p>
        <p className="mt-1 text-xs text-neutral-500">
          Trigger a ticket event with at least one active endpoint to see a
          delivery here.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
      <table className="min-w-full divide-y divide-neutral-200">
        <thead className="bg-neutral-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Created
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Endpoint
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Event
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Status
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
              HTTP
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Attempt
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Error
            </th>
            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {deliveries.map((d) => {
            const endpoint = endpointsById[d.endpoint_id];
            const canReplay = d.status === "failed_dead";
            return (
              <tr key={d.id} className="hover:bg-neutral-50">
                <td className="px-4 py-3 text-xs text-neutral-500">
                  {formatDate(d.created_at)}
                </td>
                <td className="px-4 py-3 text-xs text-neutral-800">
                  {endpoint ? (
                    <span className="font-medium">{endpoint.name}</span>
                  ) : (
                    <span className="font-mono text-neutral-500">
                      {d.endpoint_id}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-700">
                  {d.event_type}
                </td>
                <td className="px-4 py-3 text-xs">
                  <Badge variant={statusBadgeVariant(d.status)}>
                    {statusLabel(d.status)}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-xs text-neutral-700">
                  {d.http_status ?? "-"}
                </td>
                <td className="px-4 py-3 text-xs text-neutral-700">
                  {d.attempt}
                </td>
                <td
                  className="px-4 py-3 text-xs text-neutral-600"
                  title={d.error ?? ""}
                >
                  {truncate(d.error)}
                </td>
                <td className="px-4 py-3 text-right">
                  {canReplay && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onReplay(d.id)}
                      isLoading={isReplayingId === d.id}
                    >
                      Replay
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default WebhookDeliveriesTable;
